import React, { forwardRef, ReactNode, useState, useRef, useLayoutEffect, useCallback } from 'react';
import { Autocomplete } from '@mui/material';
import styles from './Field.module.scss';
import Selector, { SelectorType } from '../Selector/Selector';
import { ErrorMessageIcon } from '../ErrorMessageIcon/ErrorMessageIcon';
import { countValueChars, computeCaretPosition } from './caretPreservation';

type Props = {
  value?: string,
  onChange?: (value: string) => void,
  placeholder?: string,
  errorMessage?: string,
  warningMessage?: string,
  isEditMode?: boolean,
  style?: React.CSSProperties,
  className?: string,
  fieldOptions?: SelectorType[],
  selectedField?: SelectorType,
  onFieldChange?: (selectedField: SelectorType) => void,
  multiline?: boolean,
  icon?: ReactNode,
  statusSlot?: ReactNode,
  label?: string | ReactNode,
  required?: boolean,
  disabled?: boolean,
  type?: string,
  autocomplete?: string[],
  onBlur?: React.FocusEventHandler<HTMLInputElement>,
  unit?: string,
  onFocusStylesEnabled?: boolean,
  onFocusClassName?: string,
  onFocus?: React.FocusEventHandler<HTMLInputElement>,
  onClick?: React.MouseEventHandler<HTMLLabelElement>,
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>,
  id?: string,
  /**
   * When the onChange handler (or a formatter applied to `value`) reformats the
   * value — inserting thousand separators, a tax-number dash, stripping invalid
   * characters, etc. — a plain controlled input resets the caret to the end on
   * every keystroke. Enable this to restore the caret after the value updates,
   * anchored by the count of value characters (digits / decimal comma / leading minus)
   * to its left so it survives grouping punctuation shifting. Only works for
   * text inputs (setSelectionRange is a no-op on number/email inputs).
   */
  preserveCaret?: boolean,
}

const Field = forwardRef<HTMLInputElement, Props>(({
  id,
  value,
  onChange,
  placeholder,
  errorMessage,
  warningMessage,
  isEditMode = true,
  style,
  className,
  fieldOptions,
  selectedField,
  onFieldChange,
  multiline,
  icon,
  statusSlot,
  label,
  required,
  disabled,
  type,
  autocomplete,
  onBlur,
  unit,
  onFocusStylesEnabled,
  onFocusClassName,
  onFocus,
  onClick,
  onKeyDown,
  preserveCaret,
}, ref) => {
  const [selectedValue, _setSelectedValue] = useState(value);

  const innerRef = useRef<HTMLInputElement | null>(null);
  const caretAnchor = useRef<number | null>(null);

  const setRefs = useCallback((node: HTMLInputElement | null) => {
    innerRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
  }, [ref]);

  // After a reformatting onChange rewrites the input value, React re-sets
  // node.value which collapses the caret to the end. Restore it to the same
  // logical spot: right after the same number of value characters (digits /
  // decimal comma / leading minus), so shifting grouping punctuation doesn't
  // drag the caret around. Consumes the anchor — at most one restore per
  // keystroke, and never from a later, unrelated render.
  const restoreCaretFromAnchor = () => {
    const el = innerRef.current;
    const anchor = caretAnchor.current;
    caretAnchor.current = null;
    if (!preserveCaret || !el || anchor == null || document.activeElement !== el) return;
    const pos = computeCaretPosition(el.value, anchor);
    el.setSelectionRange(pos, pos);
  };

  useLayoutEffect(restoreCaretFromAnchor);

  const renderInput = () => {
    const input = (inputProps : any) => <input
      id={id}
      ref={setRefs}
      style={style}
      value={value}
      onChange={e => {
        if (preserveCaret) {
          const caret = e.target.selectionStart ?? e.target.value.length;
          caretAnchor.current = countValueChars(e.target.value.slice(0, caret));
          // When the formatter returns a string identical to the current
          // value, React bails out of the re-render, so the layout effect
          // never consumes the anchor — yet React still resets node.value at
          // the end of the event dispatch (caret to the end). Microtasks run
          // after both, so this repairs the caret in that bailout case and
          // guarantees the anchor never outlives its own keystroke.
          queueMicrotask(restoreCaretFromAnchor);
        }
        onChange?.(e.target.value);
      }}
      placeholder={placeholder}
      //className={styles.field}
      disabled={disabled}
      type={type}
      {...inputProps}
      className={styles.field}
      onFocus={e => {
        if (disabled) return;
        if (inputProps.onFocus) inputProps.onFocus(e);
        if (onFocus) onFocus(e);
      }}
      onBlur={e => {
        if (disabled) return;
        if (inputProps.onBlur) inputProps.onBlur(e);
        if (onBlur) onBlur(e);
      }}
      onKeyDown={e => {
        if (disabled) return;
        if (inputProps.onKeyDown) inputProps.onKeyDown(e);
        if (onKeyDown) onKeyDown(e);
      }}
    />;
    if (autocomplete) {
      return <Autocomplete
        id={id}
        options={autocomplete}
        freeSolo
        renderInput={_params => <div ref={_params.InputProps.ref}>{input(_params.inputProps)}</div>}
        inputValue={value}
        onInputChange={(_event, value) => onChange?.(value ?? '')}
        value={selectedValue} // This keeps the filtering working after you press enter
        onChange={(_event, _value) => undefined} // This keeps the filtering working after you press enter
        fullWidth
      />
    } else {
      return input({});
    }
  }

  return (
    <>
      {isEditMode ? (
      <label style={{ width: '100%' }} onClick={onClick}>
        {label && <div className={styles.label}>{label} {required ? <span className={styles.required}>*</span> : ''} </div>}
        <div className={`${styles.container} ${errorMessage ? styles.error : ''} ${multiline ? styles.textarea : ''} ${value ? styles.hasContent : ''} ${onFocusStylesEnabled ? `${styles.onFocusStyles} ${onFocusClassName}` : ''} ${className}`}>
          {selectedField && fieldOptions?.length && onFieldChange && (
            <Selector<SelectorType>
              className={styles.fieldSelector}
              isSearchable={false}
              onChange={onFieldChange}
              value={selectedField}
              options={fieldOptions}
            />
          )}
          {icon && <div className={styles.icon}>{icon}</div>}
          {multiline ? (
            <textarea
              id={id}
              style={style}
              value={value}
              onChange={e => onChange?.(e.target.value)}
              placeholder={placeholder}
              className={`${styles.field} ${styles.textarea}`}
              disabled={disabled}
              onKeyDown={onKeyDown as unknown as React.KeyboardEventHandler<HTMLTextAreaElement>}
            />
          ) : (
            renderInput()
          )}
          {unit && (
            <div className={styles.unit}>{unit}</div>
          )}
          {(errorMessage || warningMessage) && <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '4px',
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            right: '4px',
          }}>
            {errorMessage && (
              <ErrorMessageIcon
                style={{ whiteSpace: 'nowrap' }}
                errorMessage={errorMessage}
              />
            )}
            {warningMessage && (
              <ErrorMessageIcon
                style={{ whiteSpace: 'nowrap' }}
                errorMessage={warningMessage}
                isWarning={!!warningMessage}
            />)}
          </div>}
          {statusSlot && !errorMessage && !warningMessage && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              right: '10px',
            }}>
              {statusSlot}
            </div>
          )}
        </div>
      </label>
      ) : (
        <p style={style} className={`${styles.viewOnlyContainer} ${className}`}>
          <span>{value}</span>
          <input id={id} ref={ref} type="hidden" value={value} />
        </p>
      )}
    </>
  )
});

Field.displayName = 'Field';

export default Field;
